import { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { OpenInFullOutlined } from "@mui/icons-material";
import dayjs from "dayjs";

import LineChartComponent from "../../lineComponent";
import ModalChartHistory from "../../modalChartHistory";
import useStyles from "./styles";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

// Card genérico para os gráficos de tráfego do EasyFlow (Meta, Google, Netflix,
// Akamai, Globo, Amazon, TRANS/IP, IX/PTT, PNI Meta, ...). Aceita tanto um
// componente de ícone MUI quanto o src de uma imagem (logo de CDN).
// Compartilhado entre pages/internal/3.0-dashboard/3.8-flow e pages/client/easyFlow.
export default function TrafficChartCard({
  title,
  icon,
  loading = false,
  color = {},
  data = [],
  percentil = 0,
  handleAplicarZoom = () => {},
  modalColor = "blue",
  unit = "bps",
  maxValuebps = 100,
  minValuebps = 0,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [openModal, setOpenModal] = useState(false);

  const IconComponent = typeof icon !== "string" ? icon : null;

  const modalIcon = useMemo(() => {
    if (IconComponent) return IconComponent;
    return function ImageIcon() {
      return (
        <Box
          component="img"
          src={icon}
          alt={title}
          sx={{ width: 26, height: 26 }}
        />
      );
    };
  }, [IconComponent, icon, title]);

  return (
    <>
      <Paper sx={styles.paperContainer} elevation={0} variant="outlined">
        <Box sx={styles.linearProgress}>
          <Box sx={styles.boxLoading}>{loading && <LinearProgress />}</Box>
        </Box>

        <Box sx={styles.headerRow}>
          <Box sx={styles.boxTitle}>
            <Box
              sx={{
                ...styles.containerIconCard,
                backgroundColor: color?.icon,
              }}
            >
              {IconComponent ? (
                <IconComponent sx={styles.icon} />
              ) : (
                <Box
                  component="img"
                  src={icon}
                  alt={title}
                  sx={styles.iconImg}
                />
              )}
            </Box>
            <Typography sx={styles.cardSubText}>{title}</Typography>
          </Box>
          <Tooltip title="Abrir histórico ampliado">
            <IconButton
              size="small"
              onClick={() => setOpenModal(true)}
              sx={styles.historyButton}
            >
              <OpenInFullOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={styles.boxGraph}>
          <LineChartComponent
            data={data}
            isZoomEnabled={true}
            onZoomChange={(range) => {
              // Use .unix() para interpretar o valor como segundos
              const startDate = dayjs.unix(range.start).tz(timeZone);
              const endDate = dayjs.unix(range.end).tz(timeZone);

              handleAplicarZoom(startDate, endDate);
            }}
            maxValuebps={maxValuebps}
            maxTicks={10}
            mainColor={color?.graph}
            mainLineColor={color?.graph}
            xOptions={{
              tickFormatter: (clock) =>
                dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
            }}
            referenceLines={[
              {
                value: percentil,
                opt: {
                  stroke: "red",
                  label: {
                    value: "95th",
                    fill: theme.palette.mode === "dark" ? "white" : "black",
                    fontSize: "12px",
                    fontWeight: "bold",
                    position: "insideRight",
                    color: theme.palette.primary.main,
                  },
                },
              },
            ]}
          />
        </Box>
      </Paper>

      <ModalChartHistory
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={title}
        icon={modalIcon}
        color={modalColor}
        unit={unit}
        initialData={data}
        maxValuebps={maxValuebps}
        minValuebps={minValuebps}
      />
    </>
  );
}
